import type { CSSProperties } from 'react';
import { format, isValid, parse } from 'date-fns';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/*
 * Click-to-select replacements for native <input type="date"/"time"/
 * "datetime-local">. Native date/time inputs let a user type digits
 * straight into the field — these force selection through the calendar /
 * dropdown UI instead. Value formats match the native inputs exactly
 * (yyyy-MM-dd, HH:mm, yyyy-MM-ddTHH:mm) so they're a drop-in swap for
 * existing string state.
 */

const DATE_FMT = 'yyyy-MM-dd';
const DISPLAY_FMT = 'dd-MM-yyyy';
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

const parseDate = (s?: string) => {
  if (!s) return undefined;
  const d = parse(s, DATE_FMT, new Date());
  return isValid(d) ? d : undefined;
};

const triggerClass = (hasValue: boolean, className?: string) =>
  cn(
    'inline-flex cursor-pointer items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50',
    !hasValue && 'text-muted-foreground',
    className,
  );

function TimeFields({
  value,
  onChange,
  min,
  max,
}: {
  value?: string; // HH:mm
  onChange: (value: string) => void;
  min?: string; // HH:mm
  max?: string; // HH:mm
}) {
  const [h = '00', m = '00'] = value ? value.split(':') : [];
  const lo = min || '00:00';
  const hi = max || '23:59';
  const validHours = HOURS.filter((hh) => `${hh}:59` >= lo && `${hh}:00` <= hi);
  const minutesFor = (hh: string) => MINUTES.filter((mm) => `${hh}:${mm}` >= lo && `${hh}:${mm}` <= hi);
  const hourShown = value && validHours.includes(h) ? h : '';
  const minuteHour = hourShown || validHours[0] || '00';
  const validMinutes = minutesFor(minuteHour);
  const minuteShown = hourShown && validMinutes.includes(m) ? m : '';
  const pickHour = (hh: string) => {
    const mins = minutesFor(hh);
    const keep = value ? m : '00';
    onChange(`${hh}:${mins.includes(keep) ? keep : mins[0] ?? '00'}`);
  };
  return (
    <div className="flex items-center gap-1.5 p-2">
      <select
        aria-label="Hour"
        className="h-8 rounded-md border border-input bg-background px-1.5 text-sm"
        value={hourShown}
        onChange={(e) => pickHour(e.target.value)}
      >
        <option value="" disabled>
          HH
        </option>
        {validHours.map((hh) => (
          <option key={hh} value={hh}>
            {hh}
          </option>
        ))}
      </select>
      <span className="text-muted-foreground">:</span>
      <select
        aria-label="Minute"
        className="h-8 rounded-md border border-input bg-background px-1.5 text-sm"
        value={minuteShown}
        onChange={(e) => onChange(`${minuteHour}:${e.target.value}`)}
      >
        <option value="" disabled>
          MM
        </option>
        {validMinutes.map((mm) => (
          <option key={mm} value={mm}>
            {mm}
          </option>
        ))}
      </select>
    </div>
  );
}

interface BaseProps {
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
  'aria-label'?: string;
  /** Extra classes on the popover content — z-index overrides for hosts
   * that sit above Radix's default portal z-index. */
  contentClassName?: string;
}

interface CalendarDateInputProps extends BaseProps {
  value?: string; // yyyy-MM-dd
  onChange: (value: string) => void;
  min?: string; // yyyy-MM-dd
  max?: string; // yyyy-MM-dd
}

export function CalendarDateInput({
  id,
  value,
  onChange,
  min,
  max,
  className,
  placeholder = 'Select date',
  disabled,
  style,
  'aria-label': ariaLabel,
  contentClassName,
}: CalendarDateInputProps) {
  const selected = parseDate(value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button id={id} type="button" disabled={disabled} style={style} aria-label={ariaLabel} className={triggerClass(!!selected, className)}>
          <span className="truncate">{selected ? format(selected, DISPLAY_FMT) : placeholder}</span>
          <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-auto p-0', contentClassName)} align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => d && onChange(format(d, DATE_FMT))}
          {...(min ? { fromDate: parseDate(min) } : {})}
          {...(max ? { toDate: parseDate(max) } : {})}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

interface CalendarTimeInputProps extends BaseProps {
  value?: string; // HH:mm
  onChange: (value: string) => void;
  min?: string; // HH:mm
  max?: string; // HH:mm
}

export function CalendarTimeInput({
  id,
  value,
  onChange,
  min,
  max,
  className,
  placeholder = 'Select time',
  disabled,
  style,
  'aria-label': ariaLabel,
  contentClassName,
}: CalendarTimeInputProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button id={id} type="button" disabled={disabled} style={style} aria-label={ariaLabel} className={triggerClass(!!value, className)}>
          <span className="truncate">{value || placeholder}</span>
          <Clock className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-auto p-0', contentClassName)} align="start">
        <TimeFields value={value} onChange={onChange} min={min} max={max} />
      </PopoverContent>
    </Popover>
  );
}

interface CalendarDateTimeInputProps extends BaseProps {
  value?: string; // yyyy-MM-ddTHH:mm
  onChange: (value: string) => void;
  min?: string; // yyyy-MM-ddTHH:mm
  max?: string; // yyyy-MM-ddTHH:mm
}

export function CalendarDateTimeInput({
  id,
  value,
  onChange,
  min,
  max,
  className,
  placeholder = 'Select date & time',
  disabled,
  style,
  'aria-label': ariaLabel,
  contentClassName,
}: CalendarDateTimeInputProps) {
  const [datePart, timePart] = value ? value.split('T') : [undefined, undefined];
  const selected = parseDate(datePart);
  const minDate = min ? parseDate(min.split('T')[0]) : undefined;
  const maxDate = max ? parseDate(max.split('T')[0]) : undefined;

  const setDate = (d?: Date) => {
    if (!d) return;
    onChange(`${format(d, DATE_FMT)}T${timePart || '00:00'}`);
  };
  const setTime = (t: string) => {
    onChange(`${datePart || format(new Date(), DATE_FMT)}T${t}`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button id={id} type="button" disabled={disabled} style={style} aria-label={ariaLabel} className={triggerClass(!!selected, className)}>
          <span className="truncate">
            {selected ? `${format(selected, DISPLAY_FMT)} ${timePart || '00:00'}` : placeholder}
          </span>
          <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-auto p-0', contentClassName)} align="start">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          onSelect={setDate}
          {...(minDate ? { fromDate: minDate } : {})}
          {...(maxDate ? { toDate: maxDate } : {})}
          initialFocus
        />
        <div className="border-t border-border">
          <TimeFields value={timePart} onChange={setTime} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
